<template>
  <section ref="sectionRef" class="jornada">
    <div class="jornada__transition-in"></div>
    <div ref="atmosphereRef" class="jornada__atmosphere"></div>

    <div ref="triggerRef" class="jornada__trigger">
      <div class="jornada__header">
        <h2 ref="titleRef" class="jornada__title">
          Sua Jornada de Transformação
        </h2>
        <p ref="subtitleRef" class="jornada__subtitle">
          Do desafio ao crescimento exponencial.
        </p>
      </div>

      <div ref="trackRef" class="jornada__track">
        <svg
          class="jornada__line"
          viewBox="0 0 1200 4"
          preserveAspectRatio="none"
        >
          <line
            ref="lineRef"
            x1="0"
            y1="2"
            x2="1200"
            y2="2"
            stroke="url(#lineGrad)"
            stroke-width="2"
            stroke-dasharray="1200"
            stroke-dashoffset="1200"
          />
          <defs>
            <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#a78bfa" stop-opacity="0.6" />
              <stop offset="100%" stop-color="#60a5fa" stop-opacity="0.6" />
            </linearGradient>
          </defs>
        </svg>

        <div class="jornada__steps">
          <article
            v-for="(step, index) in steps"
            :key="index"
            ref="stepRefs"
            class="jornada-step"
          >
            <div class="jornada-step__number">
              {{ String(index + 1).padStart(2, "0") }}
            </div>
            <h3 class="jornada-step__title">{{ step.title }}</h3>
            <p class="jornada-step__desc">{{ step.desc }}</p>
            <div class="jornada-step__dot"></div>
          </article>
        </div>
      </div>
    </div>

    <div class="jornada__transition-out"></div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const sectionRef = ref(null);
const triggerRef = ref(null);
const trackRef = ref(null);
const atmosphereRef = ref(null);
const titleRef = ref(null);
const subtitleRef = ref(null);
const lineRef = ref(null);
const stepRefs = ref([]);

const steps = [
  {
    title: "Diagnóstico e Estratégia",
    desc: "Mapeamos seus desafios e desenhamos o plano de ação para a sua digitalização.",
  },
  {
    title: "Construção e Desenvolvimento",
    desc: "Executamos o plano com excelência técnica, construindo sua nova realidade digital.",
  },
  {
    title: "Lançamento e Tração",
    desc: "Colocamos a solução no ar e implementamos estratégias para gerar os primeiros resultados.",
  },
  {
    title: "Otimização Contínua",
    desc: "Analisamos os dados e otimizamos a operação para garantir crescimento exponencial.",
  },
];

let ctx;

onMounted(() => {
  ctx = gsap.context(() => {
    gsap.fromTo(
      titleRef.value,
      { autoAlpha: 0, y: 30 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.value,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );

    gsap.fromTo(
      subtitleRef.value,
      { autoAlpha: 0, y: 20 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        delay: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.value,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );

    gsap.to(atmosphereRef.value, {
      y: 70,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.value,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    const totalWidth = trackRef.value.scrollWidth - window.innerWidth;

    const scrollTL = gsap.timeline({
      scrollTrigger: {
        trigger: triggerRef.value,
        start: "top top",
        end: () => `+=${totalWidth}`,
        pin: true,
        scrub: 1,
        anticipatePin: 1,
      },
    });

    scrollTL.to(trackRef.value, {
      x: -totalWidth,
      ease: "none",
    });

    scrollTL.fromTo(
      lineRef.value,
      { attr: { "stroke-dashoffset": 1200 } },
      { attr: { "stroke-dashoffset": 0 }, ease: "none" },
      0
    );

    stepRefs.value.forEach((step) => {
      gsap.fromTo(
        step,
        { autoAlpha: 0, y: 40, z: -150, transformPerspective: 1200 },
        {
          autoAlpha: 1,
          y: 0,
          z: 0,
          duration: 0.6,
          ease: "power2.out",
          scrollTrigger: {
            trigger: step,
            start: "left 80%",
            end: "left 50%",
            containerAnimation: scrollTL,
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  }, sectionRef.value);
});

onUnmounted(() => {
  if (ctx) ctx.revert();
});
</script>

<style scoped>
.jornada {
  position: relative;
  overflow: hidden;
}

.jornada__transition-in {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, var(--color-bg-deep), transparent);
  z-index: 4;
  pointer-events: none;
}

.jornada__transition-out {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, transparent, var(--color-bg-deep));
  z-index: 4;
  pointer-events: none;
}

.jornada__atmosphere {
  position: fixed;
  inset: 0;
  background: radial-gradient(
      ellipse at 30% 50%,
      rgba(167, 139, 250, 0.04) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 80% 40%,
      rgba(96, 165, 250, 0.04) 0%,
      transparent 50%
    );
  z-index: 0;
  pointer-events: none;
  will-change: transform;
}

.jornada__trigger {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 4rem;
}

.jornada__header {
  text-align: center;
  padding: 0 2rem 3rem;
  position: relative;
  z-index: 5;
}

.jornada__title {
  font-size: clamp(1.8rem, 4vw, 3rem);
  color: var(--color-text-primary);
  margin-bottom: 0.75rem;
  visibility: hidden;
}

.jornada__subtitle {
  font-size: clamp(0.95rem, 1.5vw, 1.15rem);
  color: var(--color-text-secondary);
  visibility: hidden;
}

.jornada__track {
  display: flex;
  align-items: center;
  position: relative;
  width: max-content;
  padding: 0 10vw;
  min-height: 50vh;
}

.jornada__line {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 4px;
  transform: translateY(-50%);
  z-index: 2;
  filter: drop-shadow(0 0 8px rgba(167, 139, 250, 0.3));
}

.jornada__steps {
  display: flex;
  gap: 6vw;
  position: relative;
  z-index: 5;
}

.jornada-step {
  flex-shrink: 0;
  width: clamp(260px, 20vw, 320px);
  background: var(--color-bg-glass);
  border: 1px solid var(--color-border-glass);
  border-radius: 16px;
  padding: 2.5rem 2rem;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  position: relative;
  visibility: hidden;
  transition: border-color 0.4s var(--ease-out-quart),
    transform 0.4s var(--ease-out-quart), box-shadow 0.4s var(--ease-out-quart);
}

.jornada-step::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: var(--micro-grid);
  background-size: 40px 40px;
  opacity: 0;
  pointer-events: none;
  border-radius: inherit;
  transition: opacity 0.4s var(--ease-out-quart);
}

.jornada-step:hover::before {
  opacity: 1;
}

.jornada-step:hover {
  border-color: var(--color-border-glass-strong);
  transform: translateY(-4px);
  box-shadow: 0 0 40px rgba(167, 139, 250, 0.06);
}

.jornada-step__dot {
  position: absolute;
  bottom: -2rem;
  left: 50%;
  transform: translateX(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-accent);
  box-shadow: 0 0 20px var(--color-accent-glow),
    0 0 40px rgba(167, 139, 250, 0.15);
}

.jornada-step__number {
  font-family: var(--font-display);
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.04);
  margin-bottom: 1rem;
  line-height: 1;
}

.jornada-step__title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.75rem;
}

.jornada-step__desc {
  font-size: 0.88rem;
  line-height: 1.7;
  color: var(--color-text-secondary);
}

@media (max-width: 768px) {
  .jornada__track {
    padding: 0 5vw;
  }

  .jornada__steps {
    gap: 4vw;
  }

  .jornada-step {
    width: 260px;
  }
}
</style>
